import { ArrowUpRight, Ban, Cake, Mail, Phone } from "lucide-react"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  confirmationMessage,
  dispositionLine,
} from "@/lib/confirmation-message"
import { cn } from "@/lib/utils"

// System-agnostic contact-lookup review card. The target system (Filevine,
// Salesforce, HubSpot, etc.) supplies `target_label` so the resolved-state
// copy reads correctly; otherwise the card falls back to neutral phrasing.

interface CandidateMatch {
  name: string
  url?: string | null
  matchReason?: string | null
}

interface Contact {
  id: string
  name: string
  dob?: string | null
  phone?: string | null
  email?: string | null
  status: "exact" | "partial" | "new"
  match?: CandidateMatch | null
  matches?: CandidateMatch[] | null
}

interface Action {
  label: string
  message: string
  variant?: "default" | "outline"
}

interface ContactLookupData {
  title: string
  subtitle?: string
  target_label?: string | null
  contacts: Contact[]
  actions?: Action[]
}

type Resolution = "confirmed" | "create_new"

interface ContactLookupArtifactProps {
  data: string
  onAction?: (message: string) => void
}

function linkOutcomeCopy(targetLabel?: string | null): string {
  return targetLabel
    ? `This contact will be linked to the matching record in ${targetLabel}.`
    : "This contact will be linked to the matching record."
}

function newOutcomeCopy(targetLabel?: string | null): string {
  return targetLabel
    ? `A new contact will be created in ${targetLabel}.`
    : "A new contact will be created."
}

function ContactRow({
  contact,
  resolution,
  onResolve,
  onUnresolve,
  skipped,
  onSkip,
  finalized,
  targetLabel,
}: {
  contact: Contact
  resolution?: Resolution
  onResolve: (r: Resolution) => void
  onUnresolve: () => void
  skipped: boolean
  onSkip: (skipped: boolean) => void
  finalized: boolean
  targetLabel?: string | null
}) {
  const [selectedMatchIdx, setSelectedMatchIdx] = useState<number>(0)
  const [confirmedMatch, setConfirmedMatch] = useState<
    CandidateMatch | undefined
  >()

  const needsReview = contact.status === "partial" && !resolution
  const hasMultipleMatches = (contact.matches?.length ?? 0) > 1

  const isMatched =
    contact.status === "exact" ||
    (contact.status === "partial" && resolution === "confirmed")
  const isNew =
    contact.status === "new" ||
    (contact.status === "partial" && resolution === "create_new")

  const resolvedMatch = confirmedMatch ?? contact.match ?? undefined

  function handleConfirmSelection() {
    if (contact.matches && contact.matches.length > 0) {
      setConfirmedMatch(contact.matches[selectedMatchIdx])
    }
    onResolve("confirmed")
  }

  return (
    <div className="-mx-4 border-b px-4 py-4 last:border-0">
      <div className="flex min-w-0 flex-col">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  "text-sm font-semibold",
                  skipped && "text-muted-foreground",
                )}
              >
                {contact.name}
              </div>
              {needsReview && !skipped && (
                <div className="size-2 shrink-0 rounded-full bg-amber-400" />
              )}
            </div>
            {!finalized && (
              <Button
                variant="ghost"
                size="icon-sm"
                className={cn(
                  "text-muted-foreground/50 hover:text-muted-foreground size-6 shrink-0",
                  skipped && "pointer-events-none invisible",
                )}
                onClick={() => onSkip(true)}
                title="Skip this contact"
              >
                <Ban className="size-3" />
              </Button>
            )}
          </div>
          <div
            className={cn(
              "text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5",
              skipped && "opacity-40",
            )}
          >
            {contact.dob && (
              <span className="flex items-center gap-1 text-xs">
                <Cake className="size-3" />
                {contact.dob}
              </span>
            )}
            {contact.phone && (
              <span className="flex items-center gap-1 text-xs">
                <Phone className="size-3" />
                {contact.phone}
              </span>
            )}
            {contact.email && (
              <span className="flex items-center gap-1 text-xs">
                <Mail className="size-3" />
                {contact.email}
              </span>
            )}
          </div>
        </div>

        {/* Skipped */}
        {skipped && (
          <div className="mt-4 rounded-md border p-3">
            <div className="text-muted-foreground mb-1.5 text-xs font-medium">
              Skipped
            </div>
            <div className="text-muted-foreground text-xs">
              No action will be taken for this contact.
            </div>
            {!finalized && (
              <div className="flex items-center pt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground h-auto p-0 text-xs underline-offset-2 hover:bg-transparent hover:underline"
                  onClick={() => onSkip(false)}
                >
                  Undo
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Matched (exact or confirmed partial) */}
        {!skipped && isMatched && resolvedMatch && (
          <div className="mt-4 rounded-md border p-3">
            <div className="mb-1.5 text-xs font-medium text-green-700 dark:text-green-400">
              {contact.status === "exact" ? "Exact match" : "Match confirmed"}
            </div>
            <div className="flex flex-col gap-2">
              {resolvedMatch.url ? (
                <a
                  href={resolvedMatch.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-semibold no-underline hover:underline"
                >
                  {resolvedMatch.name}
                  <ArrowUpRight className="size-4" />
                </a>
              ) : (
                <span className="text-sm font-semibold">
                  {resolvedMatch.name}
                </span>
              )}
              <div className="text-muted-foreground text-xs">
                {linkOutcomeCopy(targetLabel)}
              </div>
            </div>
            {contact.status === "partial" && !finalized && (
              <div className="flex items-center pt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground h-auto p-0 text-xs underline-offset-2 hover:bg-transparent hover:underline"
                  onClick={onUnresolve}
                >
                  Undo
                </Button>
              </div>
            )}
          </div>
        )}

        {/* New contact */}
        {!skipped && isNew && (
          <div className="mt-4 rounded-md border p-3">
            <div className="text-muted-foreground mb-1.5 text-xs font-medium">
              No match
            </div>
            <div className="text-muted-foreground text-xs">
              {newOutcomeCopy(targetLabel)}
            </div>
            {contact.status === "partial" && !finalized && (
              <div className="flex items-center pt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-muted-foreground h-auto p-0 text-xs underline-offset-2 hover:bg-transparent hover:underline"
                  onClick={onUnresolve}
                >
                  Undo
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Multiple possible matches */}
        {!skipped && needsReview && hasMultipleMatches && contact.matches && (
          <div className="mt-4 rounded-md border p-3">
            <div className="mb-3 text-xs font-medium text-amber-700 dark:text-amber-400">
              {contact.matches.length} possible matches
            </div>
            <RadioGroup
              value={String(selectedMatchIdx)}
              onValueChange={(v) => setSelectedMatchIdx(Number(v))}
            >
              {contact.matches.map((match, i) => (
                <label
                  key={i}
                  htmlFor={`match-${contact.id}-${i}`}
                  className="flex cursor-pointer items-start gap-2.5"
                >
                  <RadioGroupItem
                    id={`match-${contact.id}-${i}`}
                    value={String(i)}
                    className="mt-0.5"
                  />
                  <div className="flex min-w-0 flex-col gap-1">
                    {match.url ? (
                      <a
                        href={match.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm font-semibold no-underline hover:underline"
                      >
                        {match.name}
                        <ArrowUpRight className="size-4" />
                      </a>
                    ) : (
                      <span className="text-sm font-semibold">
                        {match.name}
                      </span>
                    )}
                    {match.matchReason && (
                      <div className="text-muted-foreground text-xs">
                        {match.matchReason}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </RadioGroup>
            {!finalized && (
              <div className="flex gap-2 pt-3">
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleConfirmSelection}
                >
                  Confirm match
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => onResolve("create_new")}
                >
                  Create new
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Single possible match */}
        {!skipped && needsReview && !hasMultipleMatches && contact.match && (
          <div className="mt-4 rounded-md border p-3">
            <div className="mb-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
              Possible match
            </div>
            <div className="flex flex-col gap-2">
              {contact.match.url ? (
                <a
                  href={contact.match.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm font-semibold no-underline hover:underline"
                >
                  {contact.match.name}
                  <ArrowUpRight className="size-4" />
                </a>
              ) : (
                <span className="text-sm font-semibold">
                  {contact.match.name}
                </span>
              )}
              {contact.match.matchReason && (
                <div className="text-muted-foreground text-xs">
                  {contact.match.matchReason}
                </div>
              )}
            </div>
            {!finalized && (
              <div className="flex gap-2 pt-3">
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onResolve("confirmed")}
                >
                  Confirm match
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 text-xs"
                  onClick={() => onResolve("create_new")}
                >
                  Create new
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export function ContactLookupArtifact({
  data,
  onAction,
}: ContactLookupArtifactProps) {
  const [resolutions, setResolutions] = useState<Record<string, Resolution>>({})
  const [skippedContacts, setSkippedContacts] = useState<
    Record<string, boolean>
  >({})
  const [finalized, setFinalized] = useState(false)
  const isReadOnly = finalized || !onAction

  let parsed: ContactLookupData
  try {
    parsed = JSON.parse(data.trim()) as ContactLookupData
  } catch {
    return <code className="text-sm">{data}</code>
  }

  const allContacts = parsed.contacts
  const partialContacts = allContacts.filter((c) => c.status === "partial")
  const pendingPartial = partialContacts.filter(
    (c) => !resolutions[c.id] && !skippedContacts[c.id],
  )
  const reviewCount = pendingPartial.length

  function handleResolve(contactId: string, resolution: Resolution) {
    setResolutions((prev) => ({ ...prev, [contactId]: resolution }))
  }

  function handleUnresolve(contactId: string) {
    setResolutions((prev) => {
      const next = { ...prev }
      delete next[contactId]
      return next
    })
  }

  function handleSkip(contactId: string, skipped: boolean) {
    setSkippedContacts((prev) => ({ ...prev, [contactId]: skipped }))
  }

  function dispositionFor(contact: Contact): string {
    if (skippedContacts[contact.id]) return "Skipped"
    if (contact.status === "exact") {
      return `Linked to "${contact.match?.name ?? "matched contact"}"`
    }
    if (contact.status === "partial") {
      return resolutions[contact.id] === "confirmed"
        ? "Confirmed match"
        : "Create as new contact"
    }
    return "Create as new contact"
  }

  function handleAction(header: string) {
    // IMP-362: standardized format — a plain-language header (the button's
    // human label, never a machine token like "proceed_to_contacts"), then
    // flat "- Name → Action" dispositions.
    const items = allContacts.map((contact) =>
      dispositionLine(contact.name, dispositionFor(contact)),
    )

    setFinalized(true)
    onAction?.(confirmationMessage(header, items))
  }

  return (
    <Card className="my-3 gap-0 py-0">
      <CardHeader className="gap-1 border-b px-4 pt-4 pb-3">
        <div className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
          Contact Lookup
        </div>
        <div className="text-base font-semibold">{parsed.title}</div>
        {parsed.subtitle && (
          <div className="text-muted-foreground text-sm">{parsed.subtitle}</div>
        )}
      </CardHeader>

      <CardContent className="px-4 py-0">
        {allContacts.map((contact) => (
          <ContactRow
            key={contact.id}
            contact={contact}
            resolution={resolutions[contact.id]}
            onResolve={(r) => handleResolve(contact.id, r)}
            onUnresolve={() => handleUnresolve(contact.id)}
            skipped={!!skippedContacts[contact.id]}
            onSkip={(s) => handleSkip(contact.id, s)}
            finalized={isReadOnly}
            targetLabel={parsed.target_label}
          />
        ))}
      </CardContent>

      {!isReadOnly && parsed.actions && parsed.actions.length > 0 && (
        <CardFooter className="flex-col items-start gap-3 border-t px-4 py-4 [.border-t]:pt-4">
          {reviewCount > 0 && (
            <div className="text-muted-foreground text-xs">
              {reviewCount} partial match{reviewCount > 1 ? "es" : ""} require
              {reviewCount === 1 ? "s" : ""} review before proceeding
            </div>
          )}
          <div className="flex gap-2">
            {parsed.actions.map((action, ai) => (
              <Button
                key={ai}
                // Primary + ghost only: first (confirm) action primary, rest
                // ghost; the agent's `variant` is ignored (IMP-112).
                variant={ai === 0 ? "default" : "ghost"}
                size="sm"
                disabled={reviewCount > 0}
                onClick={() => handleAction(action.label)}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </CardFooter>
      )}
    </Card>
  )
}
